import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import b_ from 'b_';
import { useTranslation } from 'react-i18next';
import './styles.scss';
import Button from '../Button';
import Spinner from '../Spinner';
import {
  formatElapsed,
  isTransferSlow,
  isTransferTerminal,
  resolveTransfer,
} from '../../Utils/transferStatus';

const b = b_.lock('TransferTimeline');
const STAGES = ['accepted', 'prepared', 'sent', 'delivered'];

// Transfer timeline: 4 stages, a message by state, the elapsed stopwatch and the pending-gifts button
function TransferTimeline({
  status,
  error,
  startedAt,
  pollingStartedAt,
  inventoryHref,
}) {
  const { t } = useTranslation();
  const view = resolveTransfer(status, error);
  const pending = view.state === 'pending';

  const [now, setNow] = useState(() => Date.now());
  // Opened already in a state that stops polling: the end moment is unknown, no stopwatch
  const [openedTerminal] = useState(isTransferTerminal(status, error));
  // The digits freeze on an error and on success, resume when the transfer is back in progress
  // (legacy ERROR → processing) and freeze again on error → success, so "Delivered in"
  // counts up to the delivery and not up to the first error
  const [frozenAt, setFrozenAt] = useState(() => (pending ? null : Date.now()));

  useEffect(() => {
    setFrozenAt(pending ? null : Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.state]);

  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [pending]);

  const showStopwatch =
    startedAt !== null && startedAt !== undefined && !openedTerminal;
  const elapsedMs =
    (frozenAt !== null ? frozenAt : now) - (showStopwatch ? startedAt : now);
  const slow =
    pending && isTransferSlow(startedAt || pollingStartedAt || now, now);

  return (
    <div className={b()}>
      <ol className={b('stages')}>
        {STAGES.map((key, i) => {
          const n = i + 1;
          let state = 'upcoming';
          if (view.state === 'success' || n < view.stage) state = 'done';
          else if (n === view.stage)
            state = view.state === 'error' ? 'failed' : 'active';
          const label =
            state === 'active' && key !== 'delivered'
              ? t(`transfer.stages.${key}.active`)
              : t(`transfer.stages.${key}.done`);
          return (
            <li
              key={key}
              className={b('stage', { state })}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span className={b('marker')} aria-hidden="true">
                {state === 'done' && <span className={b('check')} />}
                {state === 'active' && <Spinner size="very_small" />}
                {state === 'failed' && <span className={b('cross')} />}
                {state === 'upcoming' && <span className={b('dot')} />}
              </span>
              <span className={b('label')}>{label}</span>
              {state === 'failed' && (
                <span className={b('srOnly')}>{t('transfer.failed')}</span>
              )}
            </li>
          );
        })}
      </ol>

      <div
        role="status"
        aria-live="polite"
        className={b('message', { state: view.state })}
      >
        {view.state === 'pending' && <p>{t('transfer.pending')}</p>}
        {view.state === 'pending' && slow && <p>{t('transfer.slow')}</p>}
        {view.state === 'success' && <p>{t('transfer.success')}</p>}
        {view.state === 'error' && <p>{t('transfer.error')}</p>}
      </div>

      {showStopwatch && (
        <p className={b('elapsed')} aria-hidden={pending || undefined}>
          <span>
            {t(
              view.state === 'success'
                ? 'transfer.delivered_in'
                : 'transfer.elapsed'
            )}
          </span>{' '}
          <time
            className={b('digits')}
            dateTime={`PT${Math.max(0, Math.floor(elapsedMs / 1000))}S`}
          >
            {formatElapsed(elapsedMs)}
          </time>
        </p>
      )}

      {view.state === 'success' && (
        <Button type="primary">
          <a
            className={b('link')}
            target="_blank"
            rel="noreferrer noopener"
            href={inventoryHref}
          >
            {t('transfer.open_pending_gifts')}
          </a>
        </Button>
      )}
    </div>
  );
}

TransferTimeline.defaultProps = {
  status: null,
  error: null,
  startedAt: null,
  pollingStartedAt: null,
};

TransferTimeline.propTypes = {
  status: PropTypes.string,
  error: PropTypes.object,
  /** Transfer start (backend field or localStorage); null — no stopwatch */
  startedAt: PropTypes.number,
  /** Start of polling on this page — the "taking longer" anchor when the start is unknown */
  pollingStartedAt: PropTypes.number,
  inventoryHref: PropTypes.string.isRequired,
};

export default TransferTimeline;
